/**
 * Per-viewer state (resume positions, continue watching, watch again, My List) from the CMS v1
 * `/v1/me/state` API. One account, one list: profiles are out of v1.
 *
 * A "resumption" is the rail channel (`cardToChannel`) plus its progress:
 * `{ ...channel, video_id, point, duration, progress (0-1), updatedAt, type, episode, resumption_data }`.
 * v1 cards carry no series link, so every title stands alone.
 */
import { api, unwrap, session } from '../api';
import { cardToChannel } from '../api/cards';
import analytics from './analytics';

// ponytail: one page of 50 per list (the server max); follow page.has_more if a viewer outgrows it.
const LIST_LIMIT = 50;
// The server's FINISHED_THRESHOLD: at or past this a title leaves /continue for /watch-again.
const FINISHED = 0.95;

const signedIn = () => session.getAccessToken() !== null;
const matches = (item, idOrSlug) => !!idOrSlug && (item._id === idOrSlug || item.slug === idOrSlug);

const getList = async name =>
  (await unwrap(api.GET(`/v1/me/state/${name}`, { params: { query: { limit: LIST_LIMIT } } })))
    ?.data || [];

function withProgress(item, position, duration, updatedAt) {
  const point = position ?? 0;
  const total = duration ?? 0;
  const progress = total > 0 ? point / total : 0;
  return {
    ...item,
    point,
    duration: total,
    progress,
    updatedAt,
    resumption_data: { point, duration: total, progress, updatedAt },
  };
}

function toResumption(card, type) {
  const channel = cardToChannel(card);
  const p = card.progress;
  return withProgress(
    {
      ...channel,
      video_id: card.id,
      type,
      episode: { _id: card.id, title: channel.title, duration: card.duration_s },
    },
    p?.position_s,
    p?.duration_s ?? card.duration_s,
    p?.updated_at,
  );
}

class UserDataService {
  constructor() {
    this.clearCurrentUser();
    // Sign-out (any path: logout, refresh failure, revocation) drops the previous viewer's state.
    session.subscribe(() => {
      if (!signedIn()) {
        this.clearCurrentUser();
      }
    });
  }

  clearCurrentUser() {
    this.watchlist = [];
    this.continueWatching = [];
    this.watchAgain = [];
    this._inflight = {};
    this._gen = (this._gen || 0) + 1; // a request from before a sign-out must not refill the cache
  }

  // Concurrent callers (hooks, Homepage, useStreamAuth) share one request.
  _once(key, fn) {
    if (!this._inflight[key]) {
      this._inflight[key] = fn().finally(() => {
        delete this._inflight[key];
      });
    }
    return this._inflight[key];
  }

  /** PUT /v1/me/state with one item; resolves to the authoritative row. Throws on failure. */
  async _put(mediaId, fields, init = {}) {
    const body = {
      items: [{ media_id: mediaId, ...fields, client_updated_at: new Date().toISOString() }],
    };
    const res = await unwrap(api.PUT('/v1/me/state', { body, ...init }));
    return res?.data?.items?.[0] || null;
  }

  // ---- My List -------------------------------------------------------------------------------

  /** GET /v1/me/state/watchlist into the cache. Never throws; signed out → []. */
  refreshWatchlistData() {
    return this._once('watchlist', async () => {
      if (!signedIn()) {
        this.watchlist = [];
        return [];
      }
      const gen = this._gen;
      try {
        const list = (await getList('watchlist')).map(cardToChannel);
        if (gen === this._gen) {
          this.watchlist = list;
        }
      } catch (error) {
        console.error('UserDataService: Error fetching watchlist:', error);
      }
      return this.watchlist;
    });
  }

  getWatchlist() {
    return this.refreshWatchlistData();
  }

  /** `parentIdOrSlug` is accepted for old callers; v1 has one flat list. */
  isInWatchlist(idOrSlug, parentIdOrSlug = null) {
    return this.watchlist.some(ch => matches(ch, idOrSlug) || matches(ch, parentIdOrSlug));
  }

  getWatchlistCount() {
    return this.watchlist.length;
  }

  async _setWatchlisted(idOrSlug, watchlisted) {
    if (!idOrSlug || !signedIn()) {
      return { success: false };
    }
    try {
      const row = await this._put(idOrSlug, { watchlisted });
      const success = row?.watchlisted === watchlisted;
      if (success && watchlisted) {
        // The response row has no card to render, so re-read the list.
        await this.refreshWatchlistData();
      } else if (success) {
        this.watchlist = this.watchlist.filter(
          ch => !matches(ch, idOrSlug) && !matches(ch, row.media_id),
        );
      }
      return { success, row };
    } catch (error) {
      console.error('UserDataService: Error updating watchlist:', error);
      return { success: false, error };
    }
  }

  async addToWatchlist(idOrSlug, parentIdOrSlug = null) {
    const result = await this._setWatchlisted(idOrSlug, true);
    if (result.success) {
      analytics.trackAddToWatchlist({ content_id: idOrSlug, series_id: parentIdOrSlug });
    }
    return result;
  }

  async removeFromWatchlist(idOrSlug) {
    const result = await this._setWatchlisted(idOrSlug, false);
    if (result.success) {
      analytics.trackRemoveFromWatchlist({ content_id: idOrSlug });
    }
    return result;
  }

  // ---- Continue watching / watch again -------------------------------------------------------

  _resumptions() {
    return { 'continue-watching': this.continueWatching, 'watch-again': this.watchAgain };
  }

  /** GET continue + watch-again into the cache. Never throws; signed out → empty lists. */
  refreshResumptionsData() {
    return this._once('resumptions', async () => {
      if (!signedIn()) {
        this.continueWatching = [];
        this.watchAgain = [];
        return this._resumptions();
      }
      const gen = this._gen;
      try {
        const [cont, again] = await Promise.all([getList('continue'), getList('watch-again')]);
        if (gen === this._gen) {
          this.continueWatching = cont.map(card => toResumption(card, 'continue-watching'));
          this.watchAgain = again.map(card => toResumption(card, 'watch-again'));
        }
      } catch (error) {
        console.error('UserDataService: Error refreshing resumptions:', error);
      }
      return this._resumptions();
    });
  }

  getAllResumptionsData() {
    return this.refreshResumptionsData();
  }

  async getWatchAgainData() {
    return (await this.refreshResumptionsData())['watch-again'];
  }

  getContinueWatching() {
    return this.continueWatching;
  }

  getWatchAgain() {
    return this.watchAgain;
  }

  /**
   * Save a resume position. A finished title is `positionS` = its duration (with `durationS`);
   * `positionS` 0 clears the resume point (in neither continue nor watch-again). Never throws into the
   * player: errors are logged and resolve to null.
   * @returns {Promise<object|null>} the authoritative row
   */
  async saveVideoWatchPoint(mediaIdOrSlug, positionS, { durationS, keepalive } = {}) {
    if (!mediaIdOrSlug || !Number.isFinite(positionS) || positionS < 0 || !signedIn()) {
      return null;
    }
    const fields = { position_s: Math.floor(positionS) };
    if (Number.isFinite(durationS) && durationS > 0) {
      fields.duration_s = Math.floor(durationS);
    }
    try {
      const row = await this._put(mediaIdOrSlug, fields, keepalive ? { keepalive: true } : {});
      if (row) {
        this._applyRow(mediaIdOrSlug, row);
      }
      return row;
    } catch (error) {
      console.error('UserDataService: Error saving watch point:', error);
      return null;
    }
  }

  /** Overwrite the cached continue entry from the server's row (applied or not). */
  _applyRow(sentId, row) {
    const idx = this.continueWatching.findIndex(
      item => matches(item, sentId) || matches(item, row.media_id),
    );
    if (idx === -1) {
      // ponytail: a title not yet on the rail has no card here; the next refresh brings it in.
      return;
    }
    const updated = withProgress(
      this.continueWatching[idx],
      row.position_s,
      row.duration_s ?? this.continueWatching[idx].duration,
      row.updated_at,
    );
    const rest = this.continueWatching.filter((_, i) => i !== idx);
    const finished = !updated.point || (updated.duration > 0 && updated.progress >= FINISHED);
    this.continueWatching = finished ? rest : [updated, ...rest];
  }
}

export const userDataService = new UserDataService();
export default userDataService;
